import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    __GA_ID__?: string;
    __loadGA__?: () => void;
  }
}

/**
 * SPA page_view tracking for GA4.
 * Fires a `config` + `page_view` event on every React Router location change.
 * If GA hasn't loaded yet (delayed loader in index.html), the call is queued
 * on window.dataLayer via the stub gtag() and flushed once gtag.js is ready.
 */
export const useAnalytics = () => {
  const { pathname, search } = useLocation();
  const lastPath = useRef<string | null>(null);

  useEffect(() => {
    const path = pathname + search;
    if (lastPath.current === path) return;
    const isInitial = lastPath.current === null;
    lastPath.current = path;
    // Skip the initial mount — index.html fires the first page_view once GA loads.
    if (isInitial) return;

    const gaId = window.__GA_ID__ || "G-6C5H7XZV44";
    if (typeof window.gtag !== "function") return;

    window.gtag("config", gaId, {
      page_path: path,
      page_location: window.location.href,
      page_title: document.title,
      send_page_view: false,
    });
    window.gtag("event", "page_view", {
      page_path: path,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [pathname, search]);
};

export default useAnalytics;
